import React, { useState, useEffect } from 'react';
import {
    FiBox, FiAlertTriangle, FiLayers, FiSlash, FiSearch,
    FiRefreshCw, FiTrendingUp, FiDatabase, FiArchive, FiInfo
} from 'react-icons/fi';
import { inventoryService } from '../../../services/api';
import { productService } from '../../../services/api';
import './AdminInventory.css';

const AdminInventory = () => {
    const [allProducts, setAllProducts] = useState([]);
    const [stats, setStats] = useState({
        totalProducts: 0,
        lowProductStock: 0,
        lowVariants: 0,
        outOfStock: 0
    });
    const [activeTab, setActiveTab] = useState('all');
    const [inventoryData, setInventoryData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchData = async () => {
        setIsLoading(true);
        try {
            // Unified Data Engine: All inventory derived from product catalog
            const products = await productService.getAllProducts();
            const list = Array.isArray(products) ? products : [];
            
            setAllProducts(list);

            // Calculate Metrics from Catalog dynamically
            const getStock = (p) => {
                const val = p.totalStock !== undefined ? p.totalStock : p.stock;
                return parseInt(val) || 0;
            };

            const lowStockList = list.filter(p => getStock(p) > 0 && getStock(p) < 10);
            const outOfStockList = list.filter(p => getStock(p) <= 0);
            const lowVariantsList = list.filter(p => 
                p.variants && p.variants.some(v => (v.stock || 0) < 10)
            );

            setStats({
                totalProducts: list.length,
                lowProductStock: lowStockList.length,
                lowVariants: lowVariantsList.length,
                outOfStock: outOfStockList.length
            });

            // Initial view update
            applyFilter(list, activeTab);
        } catch (err) {
            console.error('Inventory Sync Engine Failure:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const applyFilter = (source, tab) => {
        let filtered = [];
        const getStock = (p) => {
            const val = p.totalStock !== undefined ? p.totalStock : p.stock;
            return parseInt(val) || 0;
        };

        switch (tab) {
            case 'low-stock':
                filtered = source.filter(p => getStock(p) > 0 && getStock(p) < 10);
                break;
            case 'low-variants':
                filtered = source.filter(p => p.variants && p.variants.some(v => (v.stock || 0) < 10));
                break;
            case 'out-of-stock':
                filtered = source.filter(p => getStock(p) <= 0);
                break;
            default:
                filtered = source;
                break;
        }
        setInventoryData(filtered);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilter(allProducts, activeTab);
    }, [activeTab, allProducts]);

    const handleRefresh = () => {
        fetchData();
    };

    const filteredData = (inventoryData || []).filter(item => {
        if (!item) return false;
        const name = (item.title || item.name || '').toLowerCase();
        const category = (item.categoryName || '').toLowerCase();
        return name.includes(searchTerm.toLowerCase()) || category.includes(searchTerm.toLowerCase());
    });

    const getStockBadgeClass = (stock) => {
        if (stock <= 0) return 'stock-out';
        if (stock < 10) return 'stock-low';
        return 'stock-in';
    };

    return (
        <div className="admin-inventory-page">
            {/* Premium Header Banner */}
            <div className="inventory-premium-banner">
                <div className="banner-glow"></div>
                <div className="banner-content">
                    <div className="banner-tag">Stock</div>
                    <h1 className="banner-title">Manage <span>Stock</span></h1>
                    <p className="banner-subtitle">
                        See and manage all your product stock here.
                    </p>
                    <div className="banner-actions">
                        <button className="btn-refresh" onClick={handleRefresh} disabled={isLoading}>
                            <FiRefreshCw className={isLoading ? 'spinning' : ''} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Premium Stats Overview Grid */}
            <div className="products-stats-grid premium">
                <div className={`premium-stat-card ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
                    <div className="wave-bg"></div>
                    <div className="growth-badge positive">ALL</div>
                    <div className="premium-icon-box blue">
                        <FiBox className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">ALL PRODUCTS</span>
                            <span className="p-dot blue"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : stats.totalProducts}</span>
                    </div>
                </div>

                <div className={`premium-stat-card ${activeTab === 'low-stock' ? 'active' : ''}`} onClick={() => setActiveTab('low-stock')}>
                    <div className="wave-bg"></div>
                    <div className="growth-badge negative">LOW</div>
                    <div className="premium-icon-box orange">
                        <FiAlertTriangle className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">LOW STOCK</span>
                            <span className="p-dot orange"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : stats.lowProductStock}</span>
                    </div>
                </div>

                <div className={`premium-stat-card ${activeTab === 'low-variants' ? 'active' : ''}`} onClick={() => setActiveTab('low-variants')}>
                    <div className="wave-bg"></div>
                    <div className="growth-badge positive">LOW</div>
                    <div className="premium-icon-box purple">
                        <FiLayers className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">LOW VARIANTS</span>
                            <span className="p-dot purple"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : stats.lowVariants}</span>
                    </div>
                </div>

                <div className={`premium-stat-card ${activeTab === 'out-of-stock' ? 'active' : ''}`} onClick={() => setActiveTab('out-of-stock')}>
                    <div className="wave-bg"></div>
                    <div className="growth-badge negative">NONE</div>
                    <div className="premium-icon-box red">
                        <FiSlash className="p-icon" />
                    </div>
                    <div className="premium-stat-content">
                        <div className="p-label-row">
                            <span className="p-label">OUT OF STOCK</span>
                            <span className="p-dot red"></span>
                        </div>
                        <span className="p-value">{isLoading ? '...' : stats.outOfStock}</span>
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="inventory-content-card">
                <div className="content-header">
                    <div className="content-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
                            onClick={() => setActiveTab('all')}
                        >
                            All Inventory
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'low-stock' ? 'active' : ''}`}
                            onClick={() => setActiveTab('low-stock')}
                        >
                            Low Stock
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'low-variants' ? 'active' : ''}`}
                            onClick={() => setActiveTab('low-variants')}
                        >
                            Low Variants
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'out-of-stock' ? 'active' : ''}`}
                            onClick={() => setActiveTab('out-of-stock')}
                        >
                            Out of Stock
                        </button>
                    </div>

                    <div className="content-search">
                        <FiSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search inventory..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div className="inventory-table-wrapper">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Product Details</th>
                                <th>Category</th>
                                <th>Current Stock</th>
                                <th>Status</th>
                                <th>Last Updated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="skeleton-row">
                                        <td colSpan="5"><div className="skeleton-line"></div></td>
                                    </tr>
                                ))
                            ) : filteredData.length > 0 ? (
                                filteredData.map((item, idx) => (
                                    <tr key={item._id || idx}>
                                        <td>
                                            <div className="product-info-cell">
                                                <div className="product-image">
                                                    <img src={item.images?.[0] || item.image || 'https://placehold.co/50x50?text=P'} alt="" />
                                                </div>
                                                <div className="product-meta">
                                                    <span className="product-name">{item.title || item.name || 'Unknown Product'}</span>
                                                    <span className="product-id">SKU: {item.sku || item._id?.substring(18) || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="tag-category">{item.categoryName || item.category || 'Uncategorized'}</span>
                                        </td>
                                        <td>
                                            <div className="stock-cell">
                                                <span className="stock-count">{item.totalStock || item.stock || 0}</span>
                                                <span className="stock-unit">Units</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStockBadgeClass(item.totalStock || item.stock || 0)}`}>
                                                {(item.totalStock || item.stock || 0) <= 0 ? 'Out of Stock' :
                                                    (item.totalStock || item.stock || 0) < 10 ? 'Low Stock' : 'In Stock'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className="date-text">
                                                {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : 'Recent'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="5">
                                        <div className="empty-state">
                                            <FiArchive />
                                            <p>No inventory records found for this view.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminInventory;

